import { Box, Button, Callout, Card, Flex, Heading, Text } from '@radix-ui/themes'
import { DownloadIcon, UploadIcon } from '@radix-ui/react-icons'
import { useDropzone, type Accept } from 'react-dropzone'
import cn from 'classnames'
import { MessageCallout } from '~/components/MessageCallout'
import type { CsvIssue } from '../csv'

const FILE_ACCEPT: Accept = {
  'text/csv': ['.csv'],
  'application/vnd.ms-excel': ['.csv'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx']
}
const CSV_MAX_BYTES = 2 * 1024 * 1024
const VISIBLE_ISSUES = 8

const TEMPLATE_URL = `${import.meta.env.BASE_URL}installations-template.xlsx`

const IssueList = ({ issues }: { issues: CsvIssue[] }) => (
  <Callout.Root color="amber" size="1">
    <Callout.Text>
      {issues.length} row{issues.length === 1 ? ' needs' : 's need'} attention
    </Callout.Text>
    <Box asChild className="m-0 pl-4">
      <ul>
        {issues.slice(0, VISIBLE_ISSUES).map((issue, index) => (
          <li key={`${issue.rowNumber}-${issue.column}-${index}`}>
            <Text size="1">
              Row {issue.rowNumber} · <span className="font-mono">{issue.column}</span> — {issue.message}
            </Text>
          </li>
        ))}
      </ul>
    </Box>
    {issues.length > VISIBLE_ISSUES ? (
      <Callout.Text size="1">and {issues.length - VISIBLE_ISSUES} more</Callout.Text>
    ) : null}
  </Callout.Root>
)

interface CsvStepImportProps {
  description: string
  /** The file waiting to be applied, or the one already applied. */
  fileName: string | null
  isApplied: boolean
  summary: string | null
  issues: CsvIssue[]
  fatal: string[]
  error: string | null
  applyLabel: string
  applying: boolean
  disabled: boolean
  onFile: (file: File) => void
  onApply: () => void
  onClear: () => void
}

export const CsvStepImport = ({
  description,
  fileName,
  isApplied,
  summary,
  issues,
  fatal,
  error,
  applyLabel,
  applying,
  disabled,
  onFile,
  onApply,
  onClear
}: CsvStepImportProps) => {
  const { getRootProps, getInputProps, isDragActive, fileRejections } = useDropzone({
    multiple: false,
    accept: FILE_ACCEPT,
    maxSize: CSV_MAX_BYTES,
    disabled: disabled || applying,
    onDropAccepted: ([file]) => onFile(file)
  })
  const rejection = fileRejections[0]?.errors[0]?.message ?? null
  const canApply = !isApplied && fatal.length === 0 && summary !== null

  return (
    <Card>
      <Flex direction="column" gap="3">
        <Flex justify="between" align="center" wrap="wrap" gap="2">
          <Box>
            <Heading size="3">Fill from a spreadsheet</Heading>
            <Text size="1" color="gray">
              {description}
            </Text>
          </Box>
          <Button asChild variant="ghost" color="gray" size="1">
            <a href={TEMPLATE_URL} download>
              <DownloadIcon />
              Download template
            </a>
          </Button>
        </Flex>

        <Box
          {...getRootProps()}
          className={cn(
            'rounded-[var(--radius-3)] border border-dashed py-3 px-4 cursor-pointer',
            isDragActive ? 'border-[color:var(--accent-9)] bg-[var(--accent-2)]' : 'border-[color:var(--gray-6)]',
            (disabled || applying) && 'opacity-60 cursor-not-allowed'
          )}
        >
          <input {...getInputProps()} />
          <Flex align="center" gap="2">
            <UploadIcon color="var(--gray-9)" />
            <Text size="2" weight={fileName ? 'medium' : 'regular'}>
              {fileName ?? (isDragActive ? 'Drop the file here' : 'Drag an .xlsx or .csv, or click to upload')}
            </Text>
            {fileName ? (
              <Text size="1" color="gray">
                {isApplied ? 'applied · ' : ''}drop another to replace
              </Text>
            ) : null}
          </Flex>
        </Box>

        {fatal.length > 0 ? <MessageCallout color="red" message={fatal.join(' · ')} /> : null}
        {rejection ? <MessageCallout color="red" message={rejection} /> : null}
        {summary ? (
          <Text size="2" color="gray">
            {summary}
          </Text>
        ) : null}
        {issues.length > 0 ? <IssueList issues={issues} /> : null}
        {error ? <MessageCallout color="red" message={error} /> : null}

        {fileName ? (
          <Flex justify="end" gap="2">
            <Button variant="soft" color="gray" size="1" disabled={applying} onClick={onClear}>
              {isApplied ? 'Dismiss' : 'Cancel'}
            </Button>
            {canApply ? (
              <Button size="1" disabled={disabled} loading={applying} onClick={onApply}>
                {applyLabel}
              </Button>
            ) : null}
          </Flex>
        ) : null}
      </Flex>
    </Card>
  )
}
